import { useEffect, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { useIntercom } from "react-use-intercom";
import { useUserContext } from "@/contexts/UserContext";
import { API } from "@/lib/api";
import { ErrorCode } from "@boxem/core-api-schema";
import TextInput from "@/components/forms/TextInput/TextInput";
import Button from "@/components/forms/Button/Button";
import Flex from "@/components/Flex/Flex";
import Checklist from "@/components/Checklist/Checklist";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import Link from "@/components/Link/Link";
import { NotificationType, useNotificationContext } from "@/contexts/NotificationContext";
import Checkbox from "@/components/forms/Checkbox/Checkbox";

import styles from "./signup-form.module.css";
import { AFFILIATE_XID_PARAM_NAME } from "../PlanLayout";
import { logRocketError } from "@/utils/logUtils";

type SignupForm = {
  name: string;
  email: string;
  confirmEmail: string;
  password: string;
  repeat: string;
  tosAccepted: boolean;
};

export default function SignupForm() {
  const [location, navigate] = useLocation();
  const searchParams = new URLSearchParams(useSearch());
  const intercom = useIntercom();
  const { setUser } = useUserContext();
  const { showNotification } = useNotificationContext();

  const [submitButtonLoading, setSubmitButtonLoading] = useState<boolean>(false);
  const [fullNameErrorText, setFullNameErrorText] = useState<string>("");
  const [emailErrorText, setEmailErrorText] = useState<string>("");
  const [confirmEmailErrorText, setConfirmEmailErrorText] = useState<string>("");
  const [passwordErrorText, setPasswordErrorText] = useState<string>("");
  const [repeatErrorText, setRepeatErrorText] = useState<string>("");
  const [tosErrorText, setTOSErrorText] = useState<string>("");
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(true);

  const [signupForm, setSignupForm] = useState<SignupForm>({
    name: "",
    email: "",
    confirmEmail: "",
    password: "",
    repeat: "",
    tosAccepted: false,
  });
  
  const plan = searchParams.get("plan");

  //Validate all the inputs - used for validation on submit
  const validateInputs = () => {
    let valid = true;

    //Fullname rules
    if (signupForm.name === "") {
      setFullNameErrorText("Please enter your full name.");
      valid = false;
    }

    //Email rules
    if (signupForm.email === "") {
      setEmailErrorText("Email address is required.");
      valid = false;
    }
    if (!(/^[^\s@]+@[^\s@]+\.[^\s@]+$/).test(signupForm.email)) {
      setEmailErrorText("Invalid email address.");
      valid = false;
    }

    // confirm email
    if (signupForm.confirmEmail !== signupForm.email) {
      setConfirmEmailErrorText("Emails do not match.");
      valid = false;
    }

    //Password rules
    if (signupForm.password === "") {
      setPasswordErrorText("Password is required.");
      valid = false
    }
    if (signupForm.password?.length < 8 || signupForm.password?.length > 64) {
      setPasswordErrorText("Password must be between 8 and 64 characters.");
      valid = false
    }

    //Repeat rules
    if (signupForm.repeat === "") {
      setRepeatErrorText("Please confirm your password.");
      valid = false;
    }
    if (signupForm.repeat !== signupForm.password) {
      setRepeatErrorText("Password mismatch.");
      valid = false;
    }

    // accept tos checkbox
    if (!signupForm.tosAccepted) {
      setTOSErrorText("Terms of Service not accepted")
      valid = false;
    }

    //If the form was invalid then disable the submit button.
    if (!valid) {
      setSubmitButtonDisabled(true);
    }

    return valid;
  };

  //Submit form values
  const handleSubmit = async () => {
    setSubmitButtonLoading(true);

    const inputsAreValid = validateInputs();

    //If inputs aren't valid then don't continue with submit.
    if (!inputsAreValid) {
      setSubmitButtonLoading(false);
      return;
    }

    try {
      signupForm.email = signupForm.email.toLowerCase();
      const user = await API.signup({...signupForm, xid: searchParams.get(AFFILIATE_XID_PARAM_NAME) });
      setUser(user, false);

      window.LogRocket?.identify(user.uuid, {
        name: signupForm.name,
        email: signupForm.email,
        plan,
      });

      intercom.update({
        name: signupForm.name,
        email: signupForm.email,
        userId: user.uuid,
      });

      showNotification("Account has been registered.\nPlease enter your billing info to start your free trial.", NotificationType.SUCCESS);

      navigate(`~/auth/verify-email?plan=${plan}`);

    } catch (e: any) {
      if (Array.isArray(e) && e[0]?.type === ErrorCode.SIGNUP_ACCOUNT_EMAIL_ALREADY_EXISTS) {
        showNotification("Account with this email already exists!", NotificationType.ERROR);

      } else {
        showNotification("Failed to sign up!", NotificationType.ERROR);
        logRocketError(`Failed to sign up: ${e}`);
      }
    } finally {
      setSubmitButtonLoading(false);
    }
  };

  //Handle full name change
  const onFullNameChange = (name: string) => {
    setSignupForm({...signupForm, name });
    setFullNameErrorText("");
    setSubmitButtonDisabled(false);
  };

  //Handle email change
  const onEmailChange = (email: string) => {
    setSignupForm({...signupForm, email });
    setEmailErrorText("");
    setSubmitButtonDisabled(false);
  };

  //Handle email change
  const onConfirmEmailChange = (confirmEmail: string) => {
    setSignupForm({...signupForm, confirmEmail });
    setConfirmEmailErrorText("");
    setSubmitButtonDisabled(false);
  };

  //Handle password change
  const onPasswordChange = (password: string) => {
    setSignupForm({...signupForm, password });
    setPasswordErrorText("");
    setSubmitButtonDisabled(false);
  };

  //Handle repeat change
  const onRepeatChange = (repeat: string) => {
    setSignupForm({...signupForm, repeat });
    setRepeatErrorText("");
    setSubmitButtonDisabled(false);
  };

  const onAcceptTOSChange = (tosAccepted: boolean) => {
    setSignupForm({...signupForm, tosAccepted });
    setTOSErrorText("");
    setSubmitButtonDisabled(false);
  }

  //Cleanup form once comp is unmounted
  useEffect(() => {
    return () => {
      setSignupForm({
        name: "",
        email: "",
        confirmEmail: "",
        password: "",
        repeat: "",
        tosAccepted: false,
      });
    };
  }, []);

  return (
    <div className={styles.formContainer}>
      <div className={styles.inputGroup}>
        <label htmlFor="fullName">
          <Flex gap={1.5} align="center">
            <span>Full Name:</span>
          </Flex>
        </label>

        <Flex
          direction="column"
          gap={2}
          className={styles.inputContainer}
        >
          <TextInput
            className={styles.input}
            type="text"
            autocomplete="name"
            inputAutofocus={true}
            inputId="fullName"
            placeholder="ex: John Doe"
            value={signupForm.name}
            onChange={onFullNameChange}
            onSubmit={handleSubmit}
          />
        </Flex>

        {fullNameErrorText && <p className={`small-text ${styles.invalidText}`}>{fullNameErrorText}</p>}
      </div>

      <Flex gap={4}>
        <div className={styles.inputGroup}>
          <label htmlFor="email">
            <Flex gap={1.5} align="center">
              <span>Email Address:</span>
            </Flex>
          </label>

          <Flex
            direction="column"
            gap={2}
            className={styles.inputContainer}
          >
            <TextInput
              className={styles.input}
              type="email"
              autocomplete="email"  
              inputId="email"
              placeholder="your@email.com"
              value={signupForm.email}
              onChange={onEmailChange}
              onSubmit={handleSubmit}
            />
          </Flex>

          {emailErrorText && <p className={`small-text ${styles.invalidText}`}>{emailErrorText}</p>}
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="confirmEmail">
            <Flex gap={1.5} align="center">
              <span>Confirm Email Address:</span>
            </Flex>
          </label>

          <Flex
            direction="column"
            gap={2}
            className={styles.inputContainer}
          >
            <TextInput
              className={styles.input}
              type="email"
              autocomplete="email"
              inputId="confirmEmail"
              placeholder="your@email.com"
              value={signupForm.confirmEmail}
              onChange={onConfirmEmailChange}
              onSubmit={handleSubmit}
            />
          </Flex>

          {confirmEmailErrorText && <p className={`small-text ${styles.invalidText}`}>{confirmEmailErrorText}</p>}
        </div>
      </Flex>

      <Flex gap={4}>
        <div className={styles.inputGroup}>
          <label htmlFor="password">
            <Flex gap={1.5} align="center">
              <span>Password:</span>
            </Flex>
          </label>

          <Flex
            direction="column"
            gap={2}
            className={styles.inputContainer}
          >
            <TextInput
              className={styles.input}
              type="password"
              autocomplete="new-password"
              inputId="password"
              placeholder="Your password"
              value={signupForm.password}
              onChange={onPasswordChange}
              onSubmit={handleSubmit}
            />
          </Flex>

          {passwordErrorText && <p className={`small-text ${styles.invalidText}`}>{passwordErrorText}</p>}
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="repeat">
            <Flex gap={1.5} align="center">
              <span>Repeat Password:</span>
            </Flex>
          </label>

          <Flex
            direction="column"
            gap={2}
            className={styles.inputContainer}
          >
            <TextInput
              className={styles.input}
              type="password"
              autocomplete="new-password"
              inputId="repeat"
              placeholder="Repeat your password"
              value={signupForm.repeat}
              onChange={onRepeatChange}
              onSubmit={handleSubmit}
            />
          </Flex>

          {repeatErrorText && <p className={`small-text ${styles.invalidText}`}>{repeatErrorText}</p>}
        </div>
      </Flex>

      <div className={styles.checkListContainer}>
        <Checklist
          color="success"
          items={[
            { label: "Should be at least 8 characters", checked: signupForm.password?.length >= 8 },
          ]}
        />
      </div>

      <div>
        <Flex align="center" gap={2}>
          <Checkbox
            id="tosCheckbox"
            value={signupForm.tosAccepted}
            onChange={onAcceptTOSChange}
          />
          <label htmlFor="tosCheckbox" className={styles.termsOfServiceAndPrivacyPolicy}>
            I agree to the&nbsp;<a href="https://www.boxem.com/termsandconditions" target="_blank">Terms & Conditions</a>&nbsp;and
            have read through our&nbsp;<a href="https://www.boxem.com/privacypolicy" target="_blank">Privacy Policy</a>
          </label>
        </Flex>
        {tosErrorText && <p className={`small-text ${styles.invalidText}`}>{tosErrorText}</p>}

        <div className={styles.space}></div>

        <Button
          type="primary"
          disabled={submitButtonLoading || submitButtonDisabled}
          className={styles.submitButton}
          onClick={handleSubmit}
        >
          Continue
        </Button>

        <div className={styles.space}></div>

        <div className={styles.navigate}>
          Already have an account? <Link href="/auth/login">Log In</Link>
        </div>
      </div>
    </div>
  );
}
